import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../../services/auth.service';
import { useExperiment } from '../../hooks/useExperiment';
import { PreAuthCrewSetupDialog } from './PreAuthCrewSetupDialog';
import DefaultAppHeader from '../AppHeader/DefaultAppHeader';
import { GlassBox } from '../ui';
import { PageContainer } from '../PageContainer';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import { BadgeIcon } from '../../constants/icons';

import crewHomeImage from '../../assets/crew-homepage.jpg';
import competencyTreeImage from '../../assets/sample-competency-tree.jpg';

// Pain-point-led redesign for A/B test against UnauthenticatedHome (control).
// Experiment: 'landing-page-redesign-2026'
// This component is the 'treatment' variant.

const PILLARS = [
    {
        icon: <PeopleAltOutlinedIcon fontSize="large" color="primary" />,
        heading: 'Guide every member forward',
        body: 'Structured mentorship periods with matching, request workflows, and written reviews. Admins set the course; mentors and mentees just show up.',
    },
    {
        icon: <TrackChangesOutlinedIcon fontSize="large" color="primary" />,
        heading: 'Know exactly where your team stands',
        body: 'A competency framework your managers actually fill in. Level definitions per skill so assessments mean something. Gaps surface automatically - no pivot tables.',
    },
    {
        icon: <BadgeIcon fontSize="large" color="primary" />,
        heading: 'Make recognition stick',
        body: "Badges tied to skills or achievements, awarded with a reason, and visible on every member's profile - long after the Slack message scrolls away.",
    },
];

const UnauthenticatedHomeV2 = () => {
    const navigate = useNavigate();
    const { trackConversion } = useExperiment('landing-page-redesign-2026');
    const [setupOpen, setSetupOpen] = useState(false);

    const handleStart = () => {
        trackConversion();
        setSetupOpen(true);
    };

    return (
        <>
            <DefaultAppHeader />
            <PageContainer className="home" sx={{ backgroundColor: 'transparent' }}>
                <Stack spacing={2} direction="column">

                    {/* ── Hero ─────────────────────────────────────────────── */}
                    <GlassBox opacity={0.6} sx={{ p: { xs: 3, md: 6 }, width: '100%', mx: 0 }}>
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={5}>
                                <Stack spacing={3}>
                                    <Box>
                                        <Chip label="Free for teams up to 10" size="small" color="primary" variant="outlined" sx={{ mb: 2 }} />
                                        <Typography variant="h3" component="h1" fontWeight={700} sx={{ lineHeight: 1.2, mb: 2 }}>
                                            Your crew's
                                            <Box component="span" color="primary.main"> career compass.</Box>
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                            Wayfinder gives every crew member a clear picture of where they are, where they're headed, and who can help them get there - through mentorship, competency tracking, and recognition that lasts.
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={handleStart}
                                            sx={{ fontSize: '1.1rem', px: 4, py: 1.5 }}
                                        >
                                            Start for Free
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            onClick={() => navigate('/pricing')}
                                            sx={{ fontSize: '1.1rem', px: 4, py: 1.5 }}
                                        >
                                            View Pricing
                                        </Button>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        No credit card required · Free for up to 10 members · Upgrade anytime
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Box
                                    component="img"
                                    src={crewHomeImage}
                                    alt="Wayfinder manager dashboard - direct report health, assessment status, and mentorship visibility"
                                    sx={{
                                        width: '100%',
                                        borderRadius: 2,
                                        boxShadow: 6,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </GlassBox>

                    {/* ── Pain → Solution ──────────────────────────────────── */}
                    <GlassBox opacity={0.6} sx={{ p: { xs: 3, md: 6 }, width: '100%', mx: 0 }}>
                        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                            Three ways Wayfinder keeps your crew oriented
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 5 }}>
                            Growth doesn't happen by accident. Wayfinder gives managers and members the structure to make it intentional.
                        </Typography>
                        <Stack spacing={0} divider={<Divider />}>
                            {PILLARS.map(({ icon, heading, body }) => (
                                <Grid
                                    key={heading}
                                    container
                                    spacing={3}
                                    alignItems="flex-start"
                                    sx={{ py: 4 }}
                                >
                                    <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'center' } }}>
                                        {icon}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="h6" fontWeight={600} color="text.primary">
                                            {heading}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={7}>
                                        <Typography variant="body1" color="text.secondary">
                                            {body}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </Stack>
                    </GlassBox>

                    {/* ── Product screenshot: Competency Tree ──────────────── */}
                    <GlassBox opacity={0.6} sx={{ p: { xs: 3, md: 6 }, width: '100%', mx: 0 }}>
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <Box
                                    component="img"
                                    src={competencyTreeImage}
                                    alt="Wayfinder competency tree - visual skill map with assessment levels"
                                    sx={{
                                        width: '100%',
                                        borderRadius: 2,
                                        boxShadow: 6,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Stack spacing={2}>
                                    <Typography variant="overline" color="primary">Competency Framework</Typography>
                                    <Typography variant="h4" fontWeight={700}>
                                        A skill map your managers will actually use
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Define competency levels once - "Novice: writes working code with review", "Expert: designs systems others rely on" - and managers assess against those definitions, not vibes.
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Team-level coverage and gap analysis surface automatically. No pivot tables.
                                    </Typography>
                                    <Button
                                        variant="text"
                                        onClick={() => navigate('/features/competencies')}
                                        sx={{ alignSelf: 'flex-start', px: 0 }}
                                    >
                                        See how it works →
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </GlassBox>

                    {/* ── How it works ─────────────────────────────────────── */}
                    <GlassBox opacity={0.6} sx={{ p: { xs: 3, md: 6 }, width: '100%', mx: 0 }}>
                        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                            Chart your course in an afternoon
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 5 }}>
                            No implementation consultants. No 30-page admin guide.
                        </Typography>
                        <Grid container spacing={4}>
                            {[
                                {
                                    step: '01',
                                    title: 'Create your crew and invite the team',
                                    detail: 'Bulk import or share an invite link. Takes about 5 minutes.',
                                },
                                {
                                    step: '02',
                                    title: 'Define your competency framework',
                                    detail: 'Use our AI assistant to draft levels for each skill, then adjust to match how your team actually works.',
                                },
                                {
                                    step: '03',
                                    title: 'Run a mentorship period',
                                    detail: 'Set dates, let members sign up as mentors or mentees, and let the matching run. Reviews collect at the end of the period.',
                                },
                            ].map(({ step, title, detail }) => (
                                <Grid item xs={12} md={4} key={step}>
                                    <Stack spacing={1}>
                                        <Typography variant="h2" color="primary.main" fontWeight={800} sx={{ lineHeight: 1, opacity: 0.25 }}>
                                            {step}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600}>{title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{detail}</Typography>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </GlassBox>

                    {/* ── Final CTA ────────────────────────────────────────── */}
                    <GlassBox opacity={0.6} sx={{ p: { xs: 4, md: 8 }, textAlign: 'center', width: '100%', mx: 0 }}>
                        <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                            Free for teams up to 10.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                            Start today. If you grow past 10 members, it's {' '}
                            <Button variant="text" size="small" onClick={() => navigate('/pricing')} sx={{ p: 0, verticalAlign: 'baseline' }}>
                                a few dollars per person per month
                            </Button>
                            . No lock-in.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleStart}
                            sx={{ fontSize: '1.1rem', px: 5, py: 1.5 }}
                        >
                            Start Navigating Free
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                            No credit card required · Free for teams up to 10 · Upgrade anytime
                        </Typography>
                    </GlassBox>

                </Stack>
                <PreAuthCrewSetupDialog open={setupOpen} onClose={() => setSetupOpen(false)} />
            </PageContainer>
        </>
    );
};

export default UnauthenticatedHomeV2;
